import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { AgentThreadRecord } from "@/shared/domain/agentThread";

import { LaunchAgentRequest } from "../AgentRecord";

interface LaunchAgentParams {
  orgId: string;
  agentId: string;
  request: LaunchAgentRequest;
}

/**
 * Mutation hook for launching an agent - resolves variables server-side
 * and starts a new AgentSession.
 *
 * Invalidates the per-user threads list AND the org-scoped agent-session
 * search so either Sessions-tab view picks up the new run regardless of
 * which scope the user happens to be looking at.
 */
export function useLaunchAgent() {
  const { agentService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<AgentThreadRecord, Error, LaunchAgentParams>({
    mutationFn: ({ orgId, agentId, request }) =>
      agentService.launchAgent(agentId, request, { orgId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agentThreadsForUser"] });
      void queryClient.invalidateQueries({ queryKey: ["agentThreadSearch"] });
    },
  });
}
